import { JSONSchema7 } from 'json-schema';

export type BaseEntity = {
    id: string;
    created_at: Date;
    updated_at: Date;
};

export type Entity<T> = {
    [K in keyof T]: T[K];
} & BaseEntity;

export type Organization = Entity<{
    name: string;
}>;

export type User = Entity<{
    name: string;
    email: string;
    organization: Organization;
    requires_legal_document_acceptance: boolean;
    requires_subscription_acceptance: boolean;
}>;

export enum GraderType {
    CODE = 'CODE',
    PROMPT = 'PROMPT',
    BUILT_IN = 'BUILT_IN',
}

export type UnpopulatedGraderGroup = Entity<{
    organization_id: string | null;
    name: string;
    precision_explanation: string | undefined;
    recall_explanation: string | undefined;
    is_archived: boolean;
    is_editable: boolean;
}>;

export type GraderGroup = Entity<{
    organization_id: string | null;
    name: string;
    precision_explanation: string | undefined;
    recall_explanation: string | undefined;
    is_archived: boolean;
    graders: Grader[];
    created_by_user: User | null;
    is_editable: boolean;
}>;

export type Grader = Entity<{
    version: number;
    grader_group_id: string;
    type: GraderType;
    description: string;
    prompt_criteria: string | undefined;
    config_schema?: JSONSchema7;
    code?: string;
    allow_multiple_per_project: boolean;
}>;

export type ProjectGrader = Entity<{
    project_id: string;
    grader: Grader;
    name: string;
    config: Record<string, unknown>;
    minimum_precision: number;
    minimum_recall: number;
}>;

export type ProjectGraderWithGraderGroup = ProjectGrader & {
    grader_group: Omit<GraderGroup, 'graders' | 'created_by_user'>;
    is_latest_version: boolean;
};

export enum ProjectStatus {
    PENDING = 'PENDING',
    READY = 'READY',
    RUNNING = 'RUNNING',
    COMPLETED = 'COMPLETED',
    CANCELED = 'CANCELED',
    ARCHIVED = 'ARCHIVED',
}

export type Project = Entity<{
    version: number;
    project_group_id: string;
    evaluation_preferences: Record<string, unknown>;
    output_schema: JSONSchema7 | null;
    output_description: string | null;
    raw_problem_description: string | null;
    raw_context: string | null;
    recall_requirement: number;
    maximum_cost: number | null;
    maximum_latency: number | null;
    system_designs: string[];
    only_use_system_designs: boolean;
    status: ProjectStatus;
    maximum_experimentation_cost: number;
    clarification_questions?: Record<string, string[]>;
    clarification_updates?: {
        project_description: string;
        project_context: string;
        input_schema: Record<string, unknown>;
        output_schema: Record<string, unknown>;
    };
    data_cleansing_updates?: {
        project_description: string;
        project_context: string;
        input_schema: Record<string, unknown>;
        output_schema: Record<string, unknown>;
    };
    user: User;
    dataset_id?: string;
    updated_at: Date;
}>;

export type BriefProject = Omit<Project, 'user'>;

export type ProjectGroup = Entity<{
    name: string;
    description: string;
    is_archived: boolean;
    user: User;
    projects: BriefProject[];
}>;

export type ToolGroup = Entity<{
    organization_id: string | null;
    name: string;
    type: ToolType;
    tools: Tool[];
    is_archived: boolean;
    created_by_user: User | null;
    is_editable: boolean;
}>;

export type UnpopulatedToolGroup = Entity<{
    organization_id: string | null;
    name: string;
    type: ToolType;
    is_archived: boolean;
    is_editable: boolean;
}>;

export type Tool = Entity<{
    version: number;
    tool_group_id: string;
    description: string;
    base_config: Record<string, unknown>;
    config_schema: JSONSchema7;
    allow_multiple_per_project: boolean;
}>;

export enum ToolType {
    LLM = 'LLM',
    API = 'API',
}

export type ProjectTool = Entity<{
    project_id: string;
    tool: Tool;
    name: string;
    config: Record<string, unknown>;
}>;

export type ProjectToolWithToolGroup = ProjectTool & {
    tool_group: Omit<ToolGroup, 'tools' | 'created_by_user'>;
    is_latest_version: boolean;
};

export type DatasetGroup = Entity<{
    name: string;
    description: string;
    datasets: BriefDataset[];
    created_by_user: User;
    is_archived: boolean;
}>;

export enum DatasetStatus {
    PROCESSING = 'PROCESSING',
    PROCESSED = 'PROCESSED',
    AUTOLABELING_IN_PROGRESS = 'AUTOLABELING_IN_PROGRESS',
}

export enum DatasetItemStatus {
    PROCESSING = 'PROCESSING',
    PROCESSED = 'PROCESSED',
    FAILED = 'FAILED',
}

export enum DatasetItemInputStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    PROCESSED = 'PROCESSED',
    FAILED = 'FAILED',
}

export enum DocumentStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    PROCESSED = 'PROCESSED',
}

export enum DocumentSectionStatus {
    PENDING = 'PENDING',
    PROCESSED = 'PROCESSED',
}

export enum FileType {
    PDF = 'PDF',
    JPG = 'JPG',
    JPEG = 'JPEG',
    PNG = 'PNG',
}

export enum DocumentTextType {
    TYPED = 'TYPED',
    MIXED = 'MIXED',
    HANDWRITTEN = 'HANDWRITTEN',
}

export enum DocumentTextQuality {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}

export enum DocumentLayoutComplexity {
    SIMPLE = 'SIMPLE',
    MODERATE = 'MODERATE',
    COMPLEX = 'COMPLEX',
}

export type DatasetDocumentSection = Entity<{
    document_id: string;
    status: DocumentSectionStatus;
    s3_key: string;
    name: string;
    page_start: number;
    page_end: number;
    order: number;
    text_type: DocumentTextType | null;
    text_quality: DocumentTextQuality | null;
    layout_complexity: DocumentLayoutComplexity | null;
    contains_tables: boolean | null;
    contains_forms: boolean | null;
    contains_signature: boolean | null;
    contains_illegible_text: boolean | null;
    text: string | null;
}>;

export type DatasetDocument = Entity<{
    status: DocumentStatus;
    s3_path: string;
    cost_cents: number | null;
    organization_id: string;
    file_type: FileType;
    ocr_options: Record<string, unknown>;
    original_filename: string | null;
    page_count: number | null;
    processing_started_at: Date | null;
    processing_completed_at: Date | null;
    additional_data?: Record<string, unknown> | null;
    document_type?: string | null;
    presigned_url: string;
    sections: DatasetDocumentSection[];
}>;

export type DatasetItemInputInfo = Entity<{
    dataset_id: string;
    variable_name: string;
    special_data_type: SpecialDataType | null;
}>;

type DatasetItemInput = Entity<{
    info: DatasetItemInputInfo;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    raw_data: any | null;
    status: DatasetItemInputStatus;
    document: DatasetDocument | null;
    user_facing_error: string | null;
}>;

export type DatasetItem = Entity<{
    inputs: DatasetItemInput[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    output?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    proposed_output?: any;
    dataset_id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata?: Record<string, any>;
    status: DatasetItemStatus;
}>;

export type Dataset = Entity<{
    version: number;
    infos: DatasetItemInputInfo[];
    input_schema: JSONSchema7;
    output_schema: JSONSchema7 | undefined | null;
    allow_unlabeled_output: boolean;
    is_latest_version: boolean;
    dataset_group_id: string;
    status: DatasetStatus;
}>;

export type BriefDataset = Entity<{
    version: number;
    status: DatasetStatus;
}>;

export enum AutolabelingRunStatus {
    PENDING = 'PENDING',
    RUNNING = 'RUNNING',
    SUCCEEDED = 'SUCCEEDED',
    FAILED = 'FAILED',
    CANCELED = 'CANCELED',
}

export type AutolabelingRun = Entity<{
    dataset_id: string;
    project_id: string | null;
    status: AutolabelingRunStatus;
    error?: string | null;
}>;

export type ImplementationCode = Entity<{
    name: string | null;
    code: string;
    input_variable_names: string[];
    output_variable_name: string;
    description: string;
    order: number;
    code_history: {
        code: string;
        created_at: Date;
    }[];
    experiment_stage_id: string;
}>;

export type PreprocessedData = Entity<{
    variable_name: string;
    description: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
}>;

export type ToolImplementation = {
    function_name: string;
    description: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    output_schema: any;
    implementation_information: {
        system_prompt: string;
        user_message_template: string;
        output_format:
            | 'STRING'
            | 'JSON'
            | 'ARRAY'
            | 'INTEGER'
            | 'FLOAT'
            | 'BOOLEAN';
        json_schema: JSONSchema7 | null;
        model: string;
        temperature: number;
        cost: number;
        latency: number;
        inference_arguments: string[];
        thinking_enabled: boolean | null;
        reasoning_effort: 'low' | 'medium' | 'high' | null;
        max_thinking_tokens: number | null;
    };
    project_tool: Omit<ProjectToolWithToolGroup, 'is_latest_version'>;
};

export type Evaluation = Entity<{
    avg_precision: number;
    avg_recall: number;
    project_grader: ProjectGraderWithGraderGroup;
    any_answer_recall: number;
    num_items_with_precision_graded: number;
    num_items_with_recall_graded: number;
    items_with_precision_graded_aggregate_score: number;
    items_with_recall_graded_aggregate_score: number;
    created_at: Date;
    updated_at: Date;
}>;

export type Implementation = Entity<{
    name: string;
    input_schema: JSONSchema7;
    output_schema: JSONSchema7;
    implementation_codes: ImplementationCode[];
    preprocessed_data: PreprocessedData[];
    tools: ToolImplementation[];
    evaluations: Evaluation[];
    cost: number | null;
    latency: number | null;
    completed_at: Date | null;
}>;

export type ApiKey = Entity<{
    name: string;
    key_prefix: string;
    key_suffix: string;
    status: 'enabled' | 'disabled';
    created_by_user: User;
    last_used_at: Date | null;
    created_at: Date;
    updated_at: Date;
}>;

export type CreatedApiKey = ApiKey & {
    api_key: string;
};

export enum DeploymentGroupStatus {
    ENABLED = 'enabled',
    DISABLED = 'disabled',
    DELETED = 'deleted',
}

export type DeploymentGroup = Entity<{
    name: string;
    status: DeploymentGroupStatus;
    validate_input: boolean;
    validate_output: boolean;
    created_by_user: User;
    deployments: Deployment[];
}>;

export type Deployment = Entity<{
    deployment_group_id: string;
    rollout_percentage: number; // 0-10000
    version: number;
    implementation_id: string;
}>;

export type TraceGroup = Entity<{
    name: string;
    description: string | null;
    sample_rate: number; // 0-10000
    sampling_percentage: number; // 0-1
    deployment: Deployment | null;
    created_by_user: User;
}>;

export type Trace = Entity<{
    trace_group_id: string;
    start_time: Date | null;
    end_time: Date | null;
    latency: number | null;
    cents_cost: number | null;
    input_data: Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    output_data: any | null;
    error: string | null;
    attributes: Record<string, unknown>;
}>;

export type TraceWithSpans = Trace & {
    spans: Span[];
};

export enum DeploymentGroupTimeMetricsType {
    COST = 'cost',
    LATENCY = 'latency',
    CALLS = 'calls',
}

export type DeploymentGroupTimeMetricsData = {
    timestamp: Date;
    value: number;
};

export type DeploymentGroupTimeMetricsSchema = {
    data: DeploymentGroupTimeMetricsData[]; // List of data points for recharts
    interval: 'minute' | 'hour' | 'day' | 'week' | 'month';
    type: DeploymentGroupTimeMetricsType;
};

export type DeploymentGroupAvgMetricsSchema = {
    avg_cost: number; // cents
    avg_latency: number; // milliseconds
};

export enum SpanType {
    LLM = 'llm',
    TOOL = 'tool',
    FUNCTION = 'function',
}

export interface SpanBase {
    name?: string | null;
    trace_id?: string;
    type: SpanType;
    start_time?: Date | null;
    end_time?: Date | null;
    latency?: number | null;
    cents_cost?: number | null;
    order: number;
    input_data?: Record<string, unknown> | null;
    output_data?: unknown | null;
    error?: string | null;
    attributes?: Record<string, unknown> | null;
}

export interface PopulatedSpan extends SpanBase {
    id: string;
    created_at: Date;
    updated_at: Date;
    input_data: Record<string, unknown>;
    output_data: unknown;
    children: Span[];
}

export interface LlmInvocationMessage {
    id: string;
    llm_invocation_id: string;
    template: string;
    populated_message: string;
    role: string; // You might want to narrow this type if you have known roles
    input_variables: Record<string, unknown>;
    order: number;
    image_urls?: string[] | null;
    file_urls?: string[] | null;
    created_at: Date;
}

export interface LLMToolImplementationInformation {
    system_prompt: string;
    user_message_template: string;
    output_format:
        | 'STRING'
        | 'JSON'
        | 'ARRAY'
        | 'INTEGER'
        | 'FLOAT'
        | 'BOOLEAN';
    json_schema: JSONSchema7 | null;
    model: string;
    temperature: number;
    cost: number;
    latency: number;
    inference_arguments: string[];
}

export interface UnpopulatedToolImplementation {
    id: string;
    project_tool_id: string;
    function_name: string;
    description: string;
    output_schema: unknown;
    implementation_information: LLMToolImplementationInformation;
    created_at: Date;
}

export interface LLMSpan extends PopulatedSpan {
    type: SpanType.LLM;
    latency: number;
    cents_cost: number;
    input_tokens: number;
    output_tokens: number;
    temperature: number;
    model: string;
    provider: string;
    messages: LlmInvocationMessage[];
}

export interface ToolSpan extends PopulatedSpan {
    type: SpanType.TOOL;
    tool: UnpopulatedToolImplementation;
    is_cached_result: boolean;
}

export type Span = LLMSpan | ToolSpan | PopulatedSpan;

export enum SpecialDataType {
    CHAT_HISTORY = 'chat_history',
    TEXTRACT = 'textract',
    URL = 'url',
    FILE = 'file',
}

export enum SecretType {
    ANTHROPIC_API_KEY = 'ANTHROPIC_API_KEY',
    OPENAI_API_KEY = 'OPENAI_API_KEY',
    AZURE_OPENAI_API_KEY = 'AZURE_OPENAI_API_KEY',
    AZURE_FOUNDRY_API_KEY = 'AZURE_FOUNDRY_API_KEY',
    GEMINI_API_KEY = 'GEMINI_API_KEY',
    FIREWORKS_API_KEY = 'FIREWORKS_API_KEY',
    CUSTOM = 'CUSTOM',
}

export type Secret = Entity<{
    organization_id: string;
    name: string;
    type: SecretType;
    created_by_user: User;
}>;

export type SecretWithValue = Secret & {
    value: string;
};

export type ModelCost = {
    input_cost: number | string;
    output_cost: number | string;
};

export type DatasetItemWithEvaluationItems = DatasetItem & {
    evaluation_items: EvaluationItem[];
    evaluation_data?: unknown;
};

export type EvaluationItem = Entity<{
    evaluation_id: string;
    dataset_item_id: string;
    output: unknown;
    precision?: number | null;
    recall?: number | null;
    skip: boolean;
    explanation?: string | null;
}>;

export type S3PresignedPost = {
    url: string;
    fields: Record<string, string>;
};

export enum DataCleansingRunStatus {
    PENDING = 'pending',
    PREPARING = 'preparing',
    RUNNING = 'running',
    COMPLETED = 'completed',
    FAILED = 'failed',
    CANCELLED = 'cancelled',
}

export type DataCleansingRun = Entity<{
    dataset_id: string;
    project_id: string;
    status: DataCleansingRunStatus;
    started_at: Date;
    total_items: number;
    items_with_issues: number;
    completed_at: Date | null;
}>;

export type DataCleansingIssue = Entity<{
    result_id: string;
    description: string;
    comments: string | null;
    tag_id: string | null;
    is_resolved: boolean;
    metadata?: Record<string, unknown>;
}>;

export type DataCleansingResult = Entity<{
    run_id: string;
    dataset_item_id: string;
    dataset_item: DatasetItem;
    is_correct: boolean;
    subtask_count: number;
    issues: DataCleansingIssue[];
}>;

export type DataCleansingTag = Entity<{
    run_id: string;
    name: string;
    description: string;
    is_resolved: boolean;
    comments: string | null;
}>;

export type DataCleansingRunWithResults = DataCleansingRun & {
    results: DataCleansingResult[];
    tags: DataCleansingTag[];
};

// Experiment Stage Data Types for detailed evaluation view
export type ExperimentStageInputAndOutputData = {
    experiment_stage_id: string;
    input_data: Record<string, unknown>;
    output_data: Record<string, unknown>;
};

export type ToolInvocationWithToolImplementationAndLLMInvocations = Entity<{
    tool_implementation_id: string;
    experiment_stage_id: string | null;
    dataset_item_id: string | null;
    data: Record<string, unknown>;
    result: unknown;
    data_hash: string;
    is_cached_result: boolean;
    latency_seconds: number | null;
    cost_cents: number | null;
    tool_implementation: UnpopulatedToolImplementation;
    llm_invocations: LlmInvocation[];
}>;

export type LlmInvocation = Entity<{
    tool_invocation_id: string;
    model: string;
    provider: string;
    temperature: number;
    input_tokens: number;
    output_tokens: number;
    latency_seconds: number;
    cents_cost: number;
    messages: LlmInvocationMessage[];
}>;

export type ExperimentStageDataWithToolInvocations =
    ExperimentStageInputAndOutputData & {
        tool_invocations: ToolInvocationWithToolImplementationAndLLMInvocations[];
    };

export enum LegalDocumentType {
    TERMS_OF_SERVICE = 'TERMS_OF_SERVICE',
    PRIVACY_POLICY = 'PRIVACY_POLICY',
    ACCEPTABLE_USE_POLICY = 'ACCEPTABLE_USE_POLICY',
}

export type LegalDocument = {
    display_name: string;
    version: string;
    published_at: Date;
    text: string;
};

export enum BillingCadence {
    MONTHLY = 'MONTHLY',
    YEARLY = 'YEARLY',
}

export enum SubscriptionStatus {
    PENDING = 'PENDING',
    ACTIVE = 'ACTIVE',
    CANCELED = 'CANCELED',
}

export enum PlanType {
    PILOT = 'PILOT',
    STARTER = 'STARTER',
    PROFESSIONAL = 'PROFESSIONAL',
    ENTERPRISE = 'ENTERPRISE',
}

export type CurrentSubscription = Entity<{
    status: SubscriptionStatus;
    plan_type: PlanType;
    billing_cadence: BillingCadence;
    fee_cents: number;
    user_pays_compute_costs: boolean;
    number_of_projects_per_month: number;
    api_inference_calls_per_month: number;
    traces_per_month: number;
    traces_retention_days: number;
    number_of_users: number;
    auto_renew: boolean;
}>;

export type IndividualPatientData = {
    patient_name: string;
    admitted_date: string | null;
    medicard_amount: number;
    medicard_amount_with_vat: number;
    medicard_professional_fee: number;
    member_code: string | null;
    company_name: string | null;
};

export type DocumentSection = {
    section_name: string;
    page_start: number;
    page_end: number;
    section_text: string;
};

export type MedicardClaim = Entity<{
    control_code: string;
    hospital_name: string;
    medicard_received_date: Date;
    presigned_url: string;
    document_sections: DocumentSection[];
    patient_data: IndividualPatientData[];
    fuzzy_matched_patient_data: IndividualPatientData[] | null;
}>;

// Medication Claim Types
export enum MedicationClaimStatus {
    PENDING_OCR = 'PENDING_OCR',
    EXTRACTING_METADATA = 'EXTRACTING_METADATA',
    EXTRACTING_PATIENTS = 'EXTRACTING_PATIENTS',
    EXTRACTING = 'EXTRACTING',
    ENRICHING = 'ENRICHING',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum MedicationPatientStatus {
    PENDING = 'PENDING',
    EXTRACTING_MEDICATIONS = 'EXTRACTING_MEDICATIONS',
    EXTRACTING_DETAILS = 'EXTRACTING_DETAILS',
    ENRICHING_DIAGNOSES = 'ENRICHING_DIAGNOSES',
    ENRICHING_PROCEDURES = 'ENRICHING_PROCEDURES',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum MedicationStatus {
    PENDING = 'PENDING',
    NORMALIZING = 'NORMALIZING',
    MAPPING_GENERIC = 'MAPPING_GENERIC',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum DrugEnrichmentStatus {
    PENDING = 'PENDING',
    ENRICHING = 'ENRICHING',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum GenericDrugMappingStatus {
    PENDING = 'PENDING',
    MAPPING = 'MAPPING',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum RoomType {
    PRIVATE = 'private',
    SEMI_PRIVATE = 'semi_private',
    WARD = 'ward',
    ICU = 'icu',
    SUITE = 'suite',
    OBSERVATION = 'observation',
    UNKNOWN = 'unknown',
}

export type GenericDrug = Entity<{
    generic_name: string;
    description: string | null;
}>;

export type NormalizedDrug = Entity<{
    normalized_name: string;
    status: GenericDrugMappingStatus;
    generic_drug: GenericDrug | null;
}>;

export type PatientMedication = Entity<{
    patient_id: string;
    medication_name: string;
    dosage: number | null;
    dosage_unit: string | null;
    quantity: number | null;
    practitioner_name: string | null;
    total_cost: number | null;
    status: MedicationStatus;
    normalized_drug: NormalizedDrug | null;
}>;

export type PatientRoom = Entity<{
    patient_id: string;
    room_type: RoomType | null;
    price_per_day: number;
    number_of_days: number;
    total_cost: number;
}>;

export type PatientDiagnosis = Entity<{
    patient_id: string;
    diagnosis_text: string;
    icd_10_code: string | null;
}>;

export type PatientProcedure = Entity<{
    patient_id: string;
    procedure_text: string;
    cpt_code: string | null;
}>;

export type MedicationClaimDocumentSection = Entity<{
    name: string;
    s3_key: string;
    presigned_url: string;
    page_start: number;
    page_end: number;
    order: number;
    text: string | null;
}>;

export type MedicationClaimDocument = Entity<{
    s3_path: string;
    presigned_url: string;
    original_filename: string | null;
    page_count: number | null;
    sections: MedicationClaimDocumentSection[];
}>;

export type MedicationClaimPatient = Entity<{
    claim_id: string;
    patient_name: string;
    status: MedicationPatientStatus;
    medications: PatientMedication[];
    rooms: PatientRoom[];
    diagnoses: PatientDiagnosis[];
    procedures: PatientProcedure[];
}>;

export type MedicationClaim = Entity<{
    document_id: string;
    organization_id: string;
    control_code: string;
    hospital_name: string | null;
    hospital_address: string | null;
    status: MedicationClaimStatus;
    document: MedicationClaimDocument | null;
    patients: MedicationClaimPatient[];
}>;

// Medication UI Data for Analytics
export type MedicardMedicationUiDataRow = {
    patient_id: string;
    claim_id: string;
    hospital_name: string;
    unit_cost: number | null;
    normalized_name: string;
    generic_name: string;
    is_generic: boolean;
    room_type: string;
    city: string;
};

// RPM (Reliable Premium Management) Types
export type ClassCode = {
    location: string;
    state: string;
    class_code: string;
    class_description: string;
};

export type InvoiceDataRow = {
    Location: string;
    State: string;
    'Class Code': string;
    'Class Code Description': string;
};

export type InvoiceDataResult = {
    rows: InvoiceDataRow[];
    customer_name: string | null;
};

export type PayrollMetadata = {
    check_date: string | null;
    payroll_end_date: string | null;
    payroll_total: number | null;
};

export type WageEntry = {
    location: string;
    state: string;
    class_code: string;
    gross_wages: number;
    overtime_wages: number;
};

export type WageExtraction = {
    employee_name?: string;
    employee_id?: string;
    analysis: string;
    wage_data: WageEntry[];
};

export type EmployeeWageResult = {
    employee_name: string;
    employee_id?: string;
    analysis: string;
    wage_data: WageEntry[];
};

export type AggregatedTotals = {
    location: string;
    state: string;
    class_code: string;
    gross_wages: number;
    overtime_wages: number;
};

export type UnknownClassCode = {
    location: string;
    state: string;
    class_code: string;
};

export type InvalidEntry = {
    employee_name: string;
    employee_id?: string;
    original_entry: {
        location: string;
        state: string;
        class_code: string;
        gross_wages: number;
        overtime_wages: number;
    };
    coerced_entry: {
        location: string;
        state: string;
        class_code: string;
    } | null;
    reason: 'coerced_to_valid_class_code' | 'missing_coercion_result';
};

export type ClassCodeValidation = {
    extracted_unknown_codes: UnknownClassCode[];
    invalid_entries: InvalidEntry[];
    original_employee_wages:
        | Record<string, WageExtraction>
        | EmployeeWageResult[]
        | WageExtraction[];
    original_aggregated_totals: AggregatedTotals[];
    correction_method: 'llm' | 'none' | null;
};

export type WageExtractionAlertType = '1099_compensation';

export type WageExtractionAlert = {
    alert_type: WageExtractionAlertType | (string & {});
    message: string;
    evidence: string[];
};

// Wage Extraction Rules Types
export type WageExtractionRuleConditions = {
    state_in_class_codes?: string[];
    processing_notes_contains?: string[];
};

export type WageExtractionRule = Entity<{
    name: string;
    description: string | null;
    instruction_text: string;
    conditions: WageExtractionRuleConditions;
    priority: number;
    is_active: boolean;
}>;

export type AppliedRule = {
    rule_id: string;
    name: string;
    priority: number;
};

export type WageExtractionRulesResponse = {
    wage_extraction_rules: WageExtractionRule[];
};

export type WageExtractionResult = {
    payroll_report_id?: string;
    metadata: PayrollMetadata;
    employee_names?: string[];
    employee_wages:
        | Record<string, WageExtraction>
        | EmployeeWageResult[]
        | WageExtraction[];
    aggregated_totals: AggregatedTotals[];
    total_gross_wages: number;
    total_overtime_wages: number;
    presigned_url?: string;
    class_code_validation?: ClassCodeValidation;
    alerts?: WageExtractionAlert[];
    applied_rules?: AppliedRule[];
    discrepancy_explanation?: string | null;
};

// Payroll Report Types
export enum PayrollReportStatus {
    PROCESSED = 'PROCESSED',
    FAILED = 'FAILED',
}

export type PayrollReport = Entity<{
    organization_id: string;
    created_by: string;
    created_by_user: User;
    original_filename: string | null;
    s3_key: string;
    file_type: string;
    processing_notes: string | null;
    class_codes: ClassCode[] | null;
    result_payload: WageExtractionResult | null;
    latency_seconds: number | null;
    status: PayrollReportStatus;
    is_correct: boolean | null;
    feedback: string | null;
    payroll_vendor_id: string | null;
    customer_name: string | null;
}>;

export type PayrollReportListResponse = {
    payroll_reports: PayrollReport[];
    next_cursor: string | null;
};

export type PayrollReportExcelSheetDetail = {
    sheet_name: string;
    rows: string[][];
    column_headers: string[];
};

export type PayrollReportExcelDataDetail = {
    sheets: PayrollReportExcelSheetDetail[];
};

export type PayrollReportDetailResponse = {
    payroll_report: PayrollReport;
    presigned_url: string;
    excel_data: PayrollReportExcelDataDetail | null;
};

// Audio Transcription Types
export type TranscriptionRequest = {
    audio: Blob;
    post_processing?: string | null;
};

export type TranscriptionResponse = {
    transcription: string;
    pre_processed_transcription: string | null;
};

// Evaluation Run Types
export enum EvaluationRunStatus {
    PENDING = 'PENDING',
    RUNNING = 'RUNNING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
}

export enum EvaluationRunSourceType {
    EXTERNAL = 'EXTERNAL',
    INTERNAL = 'INTERNAL',
}

export type UnpopulatedProject = Entity<{
    version: number;
    project_group_id: string;
    status: ProjectStatus;
}>;

export type Experiment = Entity<{
    name: string;
    description: string | null;
}>;

export type EvaluationRun = Entity<{
    project_id: string;
    dataset_id: string;
    experiment_id: string | null;
    name: string;
    source_type: EvaluationRunSourceType;
    status: EvaluationRunStatus;
    created_by: string;
    started_at: Date | null;
    completed_at: Date | null;
    run_metadata: Record<string, unknown>;
}>;

export type EvaluationRunWithRelations = EvaluationRun & {
    project: UnpopulatedProject;
    dataset: BriefDataset;
    experiment: Experiment | null;
    created_by_user: User;
    avg_precision?: number | null;
    avg_recall?: number | null;
};

export type EvaluationWithRunId = Evaluation & {
    evaluation_run_id: string;
    project_id: string;
    experiment_id: string | null;
};

export type GraderResult = {
    project_grader_id: string;
    full_correctness_precision: number;
    avg_precision: number;
    median_precision: number;
    full_correctness_recall: number;
    avg_recall: number;
    median_recall: number;
    any_answer_recall: number;
    grading_logic: string;
    min_allowed_precision: number;
    min_allowed_recall: number;
    criteria: string;
    num_items_with_precision_graded: number;
    num_items_with_recall_graded: number;
    items_with_precision_graded_aggregate_score: number;
    items_with_recall_graded_aggregate_score: number;
};

export type DatasetItemGrade = {
    project_grader_id: string;
    precision: number;
    recall: number;
    explanation: string | null;
    criteria: string;
};

export type DatasetItemResult = {
    dataset_item_id: string;
    grades: DatasetItemGrade[];
};

export type EvaluationRunResults = {
    grader_results: GraderResult[];
    dataset_item_results: DatasetItemResult[];
};

export type EvaluationRunDetailsResponse = {
    evaluation_run: EvaluationRunWithRelations;
    evaluations: EvaluationWithRunId[];
    dataset_items: DatasetItemWithEvaluationItems[];
    results: EvaluationRunResults;
};

export type ExternalEvaluationRunsResponse = {
    runs: EvaluationRunWithRelations[];
};

// Payroll Vendor Types
export type PayrollVendor = {
    id: string;
    name: string;
    processing_instructions: string | null;
    created_at: string;
    updated_at: string;
};

export type GetPayrollVendorsResponse = {
    payroll_vendors: PayrollVendor[];
};
